import { useEffect, useState } from 'react';
import Image from 'components/ui/Image';
import Link from 'components/ui/Link';
import { formatAsShortDate } from 'utils/date';
import { useTranslation } from 'hooks/useTranslation';
import type { Author, PageAuthors } from 'types/Author';

type Props = {
  authorEntry?: PageAuthors;
  lastUpdated: string;
};

type AuthorListItemProps = {
  author: Author | null;
  isMedicalReviewer?: boolean;
};

const AuthorListItem = ({
  author,
  isMedicalReviewer = false,
}: AuthorListItemProps): JSX.Element => {
  const { locale, t } = useTranslation();

  return (
    <>
      {author && (
        <div className="flex items-center">
          {author.avatarImage && (
            <div className="rounded-full overflow-hidden w-16 h-16 mr-4">
              <Link href={author.slug}>
                <a>
                  <Image
                    src={author.avatarImage}
                    focus="face"
                    fit="thumb"
                    alt={author.name}
                    width={64}
                    height={64}
                  />
                </a>
              </Link>
            </div>
          )}
          <div>
            <h6 className="font-semibold leading-tight font-adelle">
              <Link href={author.slug}>
                <a className="text-navy">{author.name}</a>
              </Link>
            </h6>
            <p className="text-sm leading-7 text-qunoGrey">
              <Link href={author.slug}>
                <a className="text-primary">
                  {(() => {
                    if (isMedicalReviewer) {
                      return locale === 'en'
                        ? t('medicalReviewer')
                        : t('medicalReviewBy');
                    }

                    return author.description;
                  })()}
                </a>
              </Link>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const ReviewerSideLine = ({ authorEntry, lastUpdated }: Props): JSX.Element => {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [date, setDate] = useState('');

  useEffect(() => {
    if (lastUpdated) {
      setDate(formatAsShortDate(lastUpdated, locale));
    }
  }, [lastUpdated]);

  useEffect(() => {
    const navBar: number | undefined =
      document.querySelector('.navBar')?.clientHeight;
    const header: number | undefined =
      document.querySelector('header')?.clientHeight;

    if (header && navBar) {
      setHeaderHeight(header + navBar + 32);
    }
  }, []);

  const { t, locale } = useTranslation();

  return (
    <div
      className="sticky"
      style={{
        top: headerHeight ? headerHeight : 'auto',
      }}
    >
      <ul className="author-reviwer-wrap">
        <li className="mb-3">
          <p className="text-sm text-qunoGrey">{`${t('updated')}: ${date}`}</p>
        </li>
        {authorEntry && authorEntry.author && (
          <li className="mb-4">
            <AuthorListItem author={authorEntry.author} />
          </li>
        )}
        {authorEntry && authorEntry.medicalAuthor && (
          <li className="flex items-center">
            <AuthorListItem
              author={authorEntry.medicalAuthor}
              isMedicalReviewer
            />
          </li>
        )}
      </ul>
    </div>
  );
};

export default ReviewerSideLine;
